/**
 * rosterCorrections — curated corrections to the In-GO released / shiny rosters.
 *
 * Renamed from `shinyRosterOverrides` on 2026-07-16: it now corrects the base
 * released roster as well as shiny, and in BOTH directions. "Overrides" implied
 * a patch that only adds; an owner audit of every dex found the generated
 * rosters wrong both ways.
 *
 * WHY THIS EXISTS
 * ---------------
 * `generated/inGoRoster.generated.ts` is bundled from PoGoAPI. PoGoAPI is NOT a
 * complete record and is not expected to become one — as of 2026-07 its shiny
 * feed sits at 863 species while the live game is well past that, and its
 * released feed omits species outright (Orthworm #968). The gaps are structural,
 * not a lag that resolves itself. Treat it as a floor, not a source of truth.
 *
 * So this file is NOT a temporary patch waiting to be deleted — it is the
 * curated correction layer, and it is EXPECTED TO GROW as each dex is audited
 * against the live game. Adding entries is routine and must never require
 * touching logic or tests: append and go.
 *
 * WHAT THE GAME MASTER COVERS INSTEAD (do NOT duplicate here)
 * ----------------------------------------------------------
 * Niantic's Game Master authoritatively answers some questions, and those are
 * generated, never curated:
 *   - Shadow/Purified → `pokemonSettings.shadow`   (shadowRoster.generated.ts)
 *   - Tradability/Lucky → `pokemonSettings.isTradable` (tradableRoster.generated.ts)
 * It CANNOT answer released or shiny: it carries settings for unreleased species
 * (Cramorant #845 is in the master and not in the game), and holds no shiny data
 * at all. Those two are what this file is for.
 *
 * WHERE THIS IS HEADING
 * ---------------------
 * An event-driven generator (see docs/roster-sources-design.md). Until it
 * exists, in-game confirmation by the account owner is the highest-quality
 * signal available and this list carries it. Anything built later should consume
 * these entries as seed data, not discard them.
 *
 * INVARIANTS (enforced by rosterCorrections.test.ts)
 * -------------------------------------------------
 *   - `name` must match the species id, so a mistyped id in a batch is caught
 *     rather than silently unlocking the wrong Pokémon;
 *   - no duplicate ids;
 *   - a correction must actually change something (no no-op `released: true`
 *     for a species the roster already has);
 *   - `shiny: true` requires the species to end up released — a shiny cannot
 *     exist for a species the app believes is not in the game;
 *   - the client and worker copies must stay identical.
 * The tests assert PROPERTIES, never the exact contents — a new batch must not
 * turn them red.
 */

export interface RosterCorrection {
  /** National Dex id. */
  readonly id: number;
  /** Species name, for reviewability — the id alone is not human-checkable. */
  readonly name: string;
  /**
   * Force base In-GO released state.
   *   true      — released in game, the generated roster is missing it
   *   false     — NOT released, the generated roster wrongly has it
   *   undefined — no opinion; the generated roster decides
   */
  readonly released?: boolean;
  /**
   * Force shiny-released state. Same tri-state as `released`. `shiny: true`
   * implies the species is released — say so explicitly with `released: true`
   * when the base roster is missing it too (the test will tell you).
   */
  readonly shiny?: boolean;
  /** Why this correction exists, when it is not obvious. */
  readonly note?: string;
}

/**
 * Corrections confirmed in-game by the account owner — first-hand observation of
 * the live game, which outranks an incomplete third-party feed.
 *
 * Grouped by the batch they were verified in so a reviewer can see what was
 * checked when. Append new batches at the end; order does not matter to the code
 * (everything is deduped into Sets), only to the reader.
 */
export const ROSTER_CORRECTIONS: readonly RosterCorrection[] = [
  // ── Batch 1 — shiny releases verified 2026-07-15 ─────────────────────────
  // PoGoAPI's shiny feed listed none of these.
  { id: 679, name: "Honedge", shiny: true },
  { id: 680, name: "Doublade", shiny: true },
  { id: 681, name: "Aegislash", shiny: true },
  { id: 701, name: "Hawlucha", shiny: true },
  { id: 707, name: "Klefki", shiny: true },
  { id: 719, name: "Diancie", shiny: true },
  { id: 813, name: "Scorbunny", shiny: true },
  { id: 814, name: "Raboot", shiny: true },
  { id: 815, name: "Cinderace", shiny: true },
  { id: 816, name: "Sobble", shiny: true },
  { id: 817, name: "Drizzile", shiny: true },
  { id: 818, name: "Inteleon", shiny: true },
  { id: 850, name: "Sizzlipede", shiny: true },
  { id: 851, name: "Centiskorch", shiny: true },
  { id: 866, name: "Mr. Rime", shiny: true },
  { id: 874, name: "Stonjourner", shiny: true },
  { id: 901, name: "Ursaluna", shiny: true },
  { id: 948, name: "Toedscool", shiny: true },
  { id: 949, name: "Toedscruel", shiny: true },
  { id: 965, name: "Varoom", shiny: true },
  { id: 966, name: "Revavroom", shiny: true },
  {
    id: 968,
    name: "Orthworm",
    released: true,
    shiny: true,
    note: "PoGoAPI's released feed omits the species entirely, not just its shiny.",
  },

  // ── Batch 2 — full dex audit, verified 2026-07-16 ────────────────────────
  // Released in game, but the generated roster red-borders them.
  { id: 778, name: "Mimikyu", released: true },
  { id: 807, name: "Zeraora", released: true },
  { id: 824, name: "Blipbug", released: true },
  { id: 825, name: "Dottler", released: true },
  { id: 826, name: "Orbeetle", released: true },
  { id: 843, name: "Silicobra", released: true },
  { id: 844, name: "Sandaconda", released: true },
  { id: 854, name: "Sinistea", released: true },
  // Surfaced by the Lucky pass: Lucky requires the species to be released, so
  // these were red-bordered on the main dex too.
  { id: 848, name: "Toxel", released: true },
  { id: 849, name: "Toxtricity", released: true },
  { id: 931, name: "Squawkabilly", released: true },
  { id: 955, name: "Flittle", released: true },
  { id: 956, name: "Espathra", released: true },
  { id: 973, name: "Flamigo", released: true },
  // The other direction: the roster has it, the game does not.
  {
    id: 845,
    name: "Cramorant",
    released: false,
    note: "Not released in GO; PoGoAPI lists it, so we showed it with no red border.",
  },
  // #494 Victini was wrongly added as a shiny release on 2026-07-15 and is
  // corrected here rather than quietly deleted, so the check is on the record
  // and the id is not re-added by a later batch. Its shiny is NOT out.
  {
    id: 494,
    name: "Victini",
    shiny: false,
    note: "Reported shiny on 2026-07-15 in error; re-checked in game 2026-07-16.",
  },
];

const add = (pick: (c: RosterCorrection) => boolean | undefined): readonly number[] =>
  ROSTER_CORRECTIONS.filter((c) => pick(c) === true).map((c) => c.id);
const remove = (pick: (c: RosterCorrection) => boolean | undefined): readonly number[] =>
  ROSTER_CORRECTIONS.filter((c) => pick(c) === false).map((c) => c.id);

/** Ids to force INTO the base In-GO released roster. */
export const RELEASED_ADD_IDS: readonly number[] = add((c) => c.released);
/** Ids to force OUT of the base In-GO released roster. */
export const RELEASED_REMOVE_IDS: readonly number[] = remove((c) => c.released);
/** Ids to force INTO the shiny roster. */
export const SHINY_ADD_IDS: readonly number[] = add((c) => c.shiny);
/** Ids to force OUT of the shiny roster. */
export const SHINY_REMOVE_IDS: readonly number[] = remove((c) => c.shiny);

/**
 * Apply the corrections to a generated roster: additions win, removals subtract.
 * Order is deliberate — a species is never both, and the test proves it.
 */
export function applyRosterCorrections(
  generated: readonly number[],
  addIds: readonly number[],
  removeIds: readonly number[],
): number[] {
  const out = new Set<number>([...generated, ...addIds]);
  for (const id of removeIds) out.delete(id);
  // Array.from, not spread: the client tsconfig targets below es2015 and cannot
  // iterate a Set with the spread form.
  return Array.from(out);
}
